import React, { useEffect, useRef, useState } from 'react';

import swal from 'sweetalert2';
import { Drawer } from 'antd';
import { CircularProgress } from '@mui/material';
import PropTypes from 'prop-types';

import { configsManager } from 'configs/configs';
import { getConfiguration, getShopIds } from 'services/sidebar-network';

import styles from './sidebar.module.css';
import GearIcon from './components/gear';

const SideBar = ({
 theme
}) => {
  const getShopIdList = useRef(true);

  const [open, setOpen] = useState<boolean>(false);

  const [loading, setLoading] = useState<boolean>(false);

  const [configs, setConfigs] = useState<any>({
    authEndpoint: configsManager.configs.authEndpoint,
    serviceEndpoint: configsManager.configs.serviceEndpoint,
    configurationId: configsManager.configs.configurationId,
    shopId: configsManager.configs.shopId
  });

  const [shopIdOptions, setShopIdOptions] = useState<any[]>([]);
  const [configIdOptions, setConfigIdOptions] = useState<any[]>([]);

  const showDrawer = () => {
    setOpen(true);
  };

  const onClose = () => {
    setOpen(false);
    setConfigs({
      ...configs,
      authEndpoint: configsManager.configs.authEndpoint,
      serviceEndpoint: configsManager.configs.serviceEndpoint,
      configurationId: configsManager.configs.configurationId,
      shopId: configsManager.configs.shopId
    });
  };

  const getInitialShopIds = async () => {
    const response = await getShopIds();
    setShopIdOptions(response.supported_shop_ids);
  };

  const getInitialConfigIds = async () => {
    setLoading(true);
    try {
      const response = await getConfiguration(configs.shopId);
      setConfigIdOptions(response.configurations);
    } catch {
      swal.fire({
        position: 'top',
        icon: 'error',
        title: 'Error while fetching tool configurations',
        showConfirmButton: false,
        showCloseButton: true,
        background: theme==="dark"? "#161c23": "#ffffff",
        color: theme==="dark"? "#eeeeee": "unset",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSaveClick = (configurationObj: any) => {
    configurationObj.authEndpoint = configurationObj.authEndpoint.at(-1) === "/" ? configurationObj.authEndpoint.slice(0, -1).trim() : configurationObj.authEndpoint.trim();
    configurationObj.serviceEndpoint = configurationObj.serviceEndpoint.at(-1) === "/" ? configurationObj.serviceEndpoint.slice(0, -1).trim() : configurationObj.serviceEndpoint.trim();
    configsManager.setConfigs(configurationObj);
    window.location.reload();
  };

  useEffect(() => {
    const storedConfigs = JSON.parse(window.localStorage.getItem('attribute-extraction-tool-configs') || '{}');

    setConfigs({
      ...configs,
      authEndpoint: storedConfigs?.authEndpoint || configsManager.configs.authEndpoint,
      serviceEndpoint: storedConfigs?.serviceEndpoint || configsManager.configs.serviceEndpoint,
      configurationId: storedConfigs?.configurationId || configsManager.configs.configurationId,
      shopId: storedConfigs?.shopId || configsManager.configs.shopId
    });

    // TO CALL THE API ONCE WE ARE CHECKING THE VALUE FROM USE REF AND THEN SETTING IT TO FALSE AFTER CALLING ONCE
    if (getShopIdList.current) {
      getShopIdList.current = false;
      getInitialShopIds();
    }
  }, []);

  useEffect(() => {
    getInitialConfigIds();
  }, [configs.shopId]);

  return (
    <>
      <button onClick={showDrawer} className={styles.gearButton}>
        <GearIcon />
      </button>

      <Drawer
        title={'Debug Panel'}
        placement="right"
        open={open}
        closable={false}
        onClose={onClose}
        style={{ backgroundColor: theme === 'dark' ? '#161c23 ' : '', color: theme === 'dark' ? '#eeeeee' : '' }}
      >
        <>
          <div className={styles.sidebarSection}>
            <label htmlFor="service" className={styles.labelValue}>
              Labeling Tool Service Endpoint
            </label>
            <input
              type="text"
              className={styles.sidebarInputField}
              placeholder="Labeling Tool Service Endpoint"
              id="service"
              name="service"
              value={configs.authEndpoint}
              onChange={evt =>
                setConfigs({
                  ...configs,
                  authEndpoint: evt.target.value
                })
              }
            />
          </div>
          <div className={styles.sidebarSection}>
            <label htmlFor="attribute" className={styles.labelValue}>
              Attribute Extraction Tool Service Endpoint
            </label>
            <input
              type="text"
              className={styles.sidebarInputField}
              placeholder="Attribute Extraction Tool Service Endpoint"
              id="attribute"
              name="attribute"
              value={configs.serviceEndpoint}
              onChange={evt =>
                setConfigs({
                  ...configs,
                  serviceEndpoint: evt.target.value
                })
              }
            />
          </div>
          <div className={styles.sidebarSection}>
            <label htmlFor="shopIds" className={styles.labelValue}>
              Attribute Extraction Tool Shop ID
            </label>
            <select
              name={'shopIds'}
              id="shopIds"
              className={styles.sidebarInputField}
              onChange={evt => {
                setConfigs({
                  ...configs,
                  shopId: evt.target.value,
                  configurationId: ''
                });
              }}
              value={configs.shopId}
            >
              <option value="" disabled selected>
                Select Shop ID
              </option>
              {shopIdOptions?.length &&
                shopIdOptions?.map((shop, index) => {
                  return (
                    <option value={shop} key={`${shop}-${index}`}>
                      {shop}
                    </option>
                  );
                })}
            </select>
          </div>
          <div className={styles.sidebarSection}>
            <label htmlFor="configurations" className={styles.labelValue}>
              Attribute Extraction Tool Configuration
            </label>

            <select
              name={'configurations'}
              id="configurations"
              className={styles.sidebarInputField}
              onChange={evt => {
                setConfigs({
                  ...configs,
                  configurationId: evt.target.value
                });
              }}
              value={configs.configurationId}
            >
              <option value="" disabled selected>
                Select configuration
              </option>
              {configIdOptions?.length &&
                configIdOptions?.map((config, index) => {
                  return (
                    <option value={config?.configuration_id} key={`${config?.name}-${index}`}>
                      {config.name}
                    </option>
                  );
                })}
            </select>
          </div>

          <div className={styles.actionContainer}>
            <button onClick={onClose} className="btn btn-outline-dark" style={{backgroundColor: theme === "dark" ? "white" : "unset", color: theme === "dark" ? "#121212" : "unset"}}>
              Cancel
            </button>
            <button className={`btn ${styles.saveButton}`} onClick={() => handleSaveClick(configs)}>
              Save
            </button>
          </div>
        </>
        {loading && (
          <div className={styles.loaderDiv}>
            <CircularProgress color="inherit" />
            Loading Attribute Extraction Tool Configurations ...
          </div>
        )}
      </Drawer>
    </>
  );
};

SideBar.propTypes = {
  theme: PropTypes.string.isRequired
};

export default SideBar;
